import { useState, useEffect } from "react"
import axios from "axios"
import { useSelector } from "react-redux"
import Sidebar from "../components/Sidebar"
import DashboardHeader from "../components/DashboardHeader"
import QuestionCard from "../components/QuestionCard"
import AskQuestionModal from "../components/AskQuestionModal"

// API base URL - should be configured from environment variables
const BASE_URL = process.env.REACT_APP_API_BASE_URL

const QuestionsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [profilePhoto, setProfilePhoto] = useState(null);

  const user = useSelector((state) => state.auth.user);

  const userData = {
    name: "Alyxcyz May Servino",
    initials: "AM",
    notificationCount: 4,
  }

  // Fetch profile photo separately
    useEffect(() => {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem("access");
          const res = await axios.get(`${BASE_URL}/users/profile/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const p = res.data.profile_photo;
          if (p) {
            setProfilePhoto(
              p.startsWith("http") ? p : `${BASE_URL}${p}`
            );
          } else {
            setProfilePhoto(null);
          }
        } catch (e) {
          setProfilePhoto(null);
        }
      };
      fetchUserProfile();
    }, []);

  useEffect(() => {
    fetchQuestions()
  }, [])

  const fetchQuestions = async () => {
    setLoading(true)
    setError(null)

    try {
      // Get token from localStorage
      const token = localStorage.getItem("access")

      // Make API call with authorization header
      const response = await axios.get(`${BASE_URL}/users/questions/`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setQuestions(response.data)
    } catch (err) {
      //console.error("Error fetching questions:", err)
      setError("Failed to load questions. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  const handleAskQuestion = async (questionData) => {
    try {
      const token = localStorage.getItem("access")

      // Submit question to API
      await axios.post(`${BASE_URL}/users/questions/`,
        { question_text: questionData.questionText, category: questionData.category },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Refresh questions list
      fetchQuestions()
      setShowModal(false)

      // Could add a success notification here
    } catch (err) {
      //console.error("Error submitting question:", err)
      // Could add error handling/notification here
      return { error: "Failed to submit question. Please try again." }
    }
  }

  const getStatus = (q) => q.status || (q.answer_text ? "answered" : "pending");

  const filteredQuestions = questions.filter((question) => {
    const status = getStatus(question);
    const matchesSearch = question.question_text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || status === filterStatus;
    return matchesSearch && matchesFilter;
  });


  const getStatusCounts = () => {
    return {
      all: questions.length,
      answered: questions.filter((q) => (q.status || (q.answer_text ? 'answered' : 'pending')) === "answered").length,
      pending: questions.filter((q) => (q.status || (q.answer_text ? 'answered' : 'pending')) === "pending").length,
    }
  }

  const statusCounts = getStatusCounts()

  return (
    <div className="dashboard-container">
      <div className="d-flex">
        <Sidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed((prev) => !prev)}/>
        <div className="main-content flex-grow-1">
          <DashboardHeader user={user} profilePhoto={profilePhoto} onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
          <div className="dashboard-content p-4">
            <div className="questions-header mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="dashboard-title mb-0">Question & Answer</h2>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                  <i className="fas fa-plus me-2"></i>
                  Ask a Question
                </button>
              </div>

              {/* Stats Cards */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="stat-card-small">
                    <div className="stat-icon">
                      <i className="fas fa-question-circle text-primary"></i>
                    </div>
                    <div className="stat-info">
                      <div className="stat-number">{statusCounts.all}</div>
                      <div className="stat-label">Total Questions</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card-small">
                    <div className="stat-icon">
                      <i className="fas fa-check-circle text-success"></i>
                    </div>
                    <div className="stat-info">
                      <div className="stat-number">{statusCounts.answered}</div>
                      <div className="stat-label">Answered</div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="stat-card-small">
                    <div className="stat-icon">
                      <i className="fas fa-clock text-warning"></i>
                    </div>
                    <div className="stat-info">
                      <div className="stat-number">{statusCounts.pending}</div>
                      <div className="stat-label">Pending</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="questions-controls">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="search-box">
                      <i className="fas fa-search search-icon"></i>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <select
                      className="form-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Questions</option>
                      <option value="answered">Answered</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="questions-list">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading your questions...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                  <button className="btn btn-outline-danger btn-sm ms-3" onClick={fetchQuestions}>
                    <i className="fas fa-sync-alt me-1"></i> Retry
                  </button>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div className="empty-state">
                  <div className="text-center py-5">
                    <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">
                      {searchTerm || filterStatus !== "all" ? "No questions found" : "No questions yet"}
                    </h5>
                    <p className="text-muted">
                      {searchTerm || filterStatus !== "all"
                        ? "Try adjusting your search or filter criteria."
                        : "Ask your first question to get started!"}
                    </p>
                    {!searchTerm && filterStatus === "all" && (
                      <button className="btn btn-primary mt-3" onClick={() => setShowModal(true)}>
                        <i className="fas fa-plus me-2"></i>
                        Ask a Question
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="row">
                  {filteredQuestions.map((question) => (
                    <div key={question.id} className="col-12 mb-3">
                      <QuestionCard question={question} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AskQuestionModal show={showModal} onHide={() => setShowModal(false)} onSubmit={handleAskQuestion} />
    </div>
  )
}

export default QuestionsPage
